import {
  Viewer,
  Cartesian2,
  Color,
  Entity,
  Billboard,
  CustomDataSource,
  HeightReference,
  EntityCluster,
} from "cesium";
import { point } from "@turf/helpers";
import distance from "@turf/distance";
import { AttachablePluginInterface } from "./CesiumWrapper";
import ScreenSpaceEventsPlugin from "./ScreenSpaceEventsPlugin";
import CameraPlugin from "./CameraPlugin";
import { MarkerEntityType } from "../utils/MarkerEntityType";
import { CesiumCursor } from "../utils/CesiumCursor";
import { generatePrimaryMarker } from "../utils/generatePrimaryMarker";
import {
  generateClusterMarker,
  generateUnfoldedClusterMarker,
} from "../utils/generateClusterMarker";
import { generateSecondaryMarker } from "../utils/generateSecondaryMarker";
import { generateMarkerTooltip } from "../utils/generateMarkerTooltip";

export type MarkerModel = {
  coordinates: number[];
  markerScale?: number;
  markerColor: string;
  tooltipText: string;
  onClick(id?: number): void;
};

class ClusterProperties {
  entities: Entity[];
  isFoldable: boolean;
  cursor: CesiumCursor;
}

export default class GeographicMarkersPlugin
  implements AttachablePluginInterface
{
  private _viewer: Viewer = null;
  private _markersDataSource: CustomDataSource = null;
  private _unfoldedClusterBillboard: Billboard = null;
  private _unfoldedMarkerEntities: Entity[] = [];
  private _tooltipEntity: Entity = null;
  private _detachClickHandlerFunc: () => void;
  private _detachMoveHandlerFunc: () => void;
  private _detachClusterHandlerFunc: () => void;

  constructor(
    private _ssePlugin: ScreenSpaceEventsPlugin,
    private _cameraPlugin: CameraPlugin,
  ) {
    this._markersDataSource = new CustomDataSource("markers");
    this._markersDataSource.clustering.enabled = true;
    this._markersDataSource.clustering.pixelRange = 100;
    this._markersDataSource.clustering.minimumClusterSize = 2;
    this.attachClusterHandler();
  }

  setViewer(viewer: Viewer) {
    this._viewer = viewer;
    this._viewer.dataSources.add(this._markersDataSource);
  }

  detachViewer() {
    this._viewer?.dataSources.remove(this._markersDataSource, false);
    this._unfoldedClusterBillboard = null;
    this.destroyUnfoldedMarkers();
    this.destroyTooltip();
    this._viewer = null;
  }

  setMarkers(markers: MarkerModel[]) {
    this._markersDataSource.entities.removeAll();
    this._unfoldedClusterBillboard = null;
    this.destroyUnfoldedMarkers();
    this.destroyTooltip();

    markers?.forEach((marker) => {
      const { coordinates, onClick, tooltipText, markerScale, markerColor } =
        marker;
      const primaryMarker = generatePrimaryMarker(
        coordinates,
        onClick,
        markerScale,
        markerColor,
        tooltipText,
      );
      this._markersDataSource.entities.add(primaryMarker);
    });

    if (this._markersDataSource.entities.values.length > 0) {
      this.attachClickHandler();
      this.attachMoveHandler();
    } else {
      this._detachClickHandlerFunc?.();
      this._detachMoveHandlerFunc?.();
    }
    this._viewer?.scene.requestRender();
  }

  clearMarkers() {
    this._markersDataSource.entities.removeAll();
    this._unfoldedClusterBillboard = null;
    this.destroyUnfoldedMarkers();
    this.destroyTooltip();
    this._detachClickHandlerFunc?.();
    this._detachMoveHandlerFunc?.();
  }

  private attachClusterHandler() {
    const callback: EntityCluster.newClusterCallback = (
      clusteredEntities,
      cluster,
    ) => {
      const { label, billboard } = cluster;
      billboard.show = true;
      billboard.heightReference = HeightReference.CLAMP_TO_GROUND;
      billboard.disableDepthTestDistance = Number.POSITIVE_INFINITY;
      label.show = false;
      label.heightReference = HeightReference.CLAMP_TO_GROUND;
      label.disableDepthTestDistance = Number.POSITIVE_INFINITY;
      label.backgroundColor = Color.WHITE;

      //check if all child markers of the cluster are within one meter of each other
      const coordinates: number[][] = clusteredEntities.map((entity) =>
        entity.properties.coordinates.valueOf(),
      );
      const isFoldable = coordinates.every((val, i, arr) => {
        const origin = point(arr[0]);
        const distanceToOrigin = distance(point(val), origin, {
          units: "meters",
        });
        return distanceToOrigin <= 1;
      });

      const properties = new ClusterProperties();
      properties.entities = clusteredEntities;
      properties.isFoldable = isFoldable;
      properties.cursor = CesiumCursor.pointer;

      billboard.id = properties;
      billboard.scale = 0.5;
      billboard.image = generateClusterMarker(clusteredEntities.length);
    };

    this._detachClusterHandlerFunc?.();
    this._detachClusterHandlerFunc =
      this._markersDataSource.clustering.clusterEvent.addEventListener(
        callback,
      );
  }

  private attachClickHandler() {
    this._detachClickHandlerFunc?.();
    this._detachClickHandlerFunc = this._ssePlugin.leftClickEvent.addHandler(
      (event) => {
        if (!this._viewer) return;

        this.foldCluster();
        this.destroyTooltip();

        const pick = this._viewer.scene.drillPick(event.position, 1)[0];
        const pickIdObject = pick?.id;
        const pickPrimitive = pick?.primitive;
        if (
          pickPrimitive instanceof Billboard &&
          pickIdObject instanceof ClusterProperties
        ) {
          //clicked a cluster marker
          const cluster = pickIdObject;
          const billboard = pickPrimitive;
          if (cluster.isFoldable) {
            this.unfoldCluster(cluster, billboard);
          } else {
            this._cameraPlugin.flyToEntities(cluster.entities);
          }
        } else if (
          pickPrimitive instanceof Billboard &&
          pickIdObject instanceof Entity &&
          [
            MarkerEntityType.PrimaryMarker,
            MarkerEntityType.SecondaryMarker,
          ].includes(pickIdObject.properties?.type?.valueOf())
        ) {
          //clicked an actual marker
          const entity = pickIdObject;
          const onClick = entity.properties?.onClick?.getValue();
          onClick?.();
        } else {
          //clicked anything or nothing
          return;
        }
      },
    );
  }

  private attachMoveHandler() {
    this._detachMoveHandlerFunc?.();
    this._detachMoveHandlerFunc = this._ssePlugin.mouseMoveEvent.addHandler(
      (event) => {
        if (!this._viewer) return;

        const pick = this._viewer.scene.drillPick(event.endPosition, 1)[0];
        const pickIdObject = pick?.id;
        if (
          pickIdObject instanceof Entity &&
          pickIdObject.properties?.tooltipText?.getValue()
        ) {
          //mouse moved over an entity with a tooltip
          if (this._tooltipEntity) return;
          const entity = pickIdObject;
          this.createTooltip(entity);
        } else {
          //mouse moved over anything else
          this.destroyTooltip();
        }
      },
    );
  }

  private unfoldCluster(cluster: ClusterProperties, billboard: Billboard) {
    this.createUnfoldedMarkers(cluster.entities);
    billboard.setImage("unfolded", generateUnfoldedClusterMarker());
    this._unfoldedClusterBillboard = billboard;
    this._viewer.scene.requestRender();
  }

  private foldCluster() {
    if (this._unfoldedClusterBillboard) {
      const cluster = this._unfoldedClusterBillboard.id as ClusterProperties;
      this._unfoldedClusterBillboard.setImage(
        "folded",
        generateClusterMarker(cluster.entities.length),
      );
      this._unfoldedClusterBillboard = null;
    }
    this.destroyUnfoldedMarkers();
  }

  private createUnfoldedMarkers(entities: Entity[]) {
    const origin = entities[0].properties.coordinates.valueOf();
    entities.forEach((entity, i) => {
      const markerSpread = 60;
      const markerOffsetX =
        (markerSpread / -2) * entities.length +
        markerSpread / 2 +
        i * markerSpread;
      const markerOffsetY = -70;
      const childMarkerPixelOffset = new Cartesian2(
        markerOffsetX,
        markerOffsetY,
      );
      const secondaryMarker = generateSecondaryMarker(
        entity,
        origin,
        childMarkerPixelOffset,
      );
      this._unfoldedMarkerEntities.push(
        this._viewer.entities.add(secondaryMarker),
      );
    });
    this._viewer.scene.requestRender();
  }

  private createTooltip(entity: Entity) {
    const tooltipMarker = generateMarkerTooltip(
      entity.properties.coordinates.valueOf(),
      entity.properties.tooltipText.valueOf(),
      entity.properties.tooltipPixelOffset.valueOf(),
    );
    this._tooltipEntity = this._viewer.entities.add(tooltipMarker);
    this._viewer.scene.requestRender();
  }

  private destroyUnfoldedMarkers() {
    if (this._viewer && !!this._unfoldedMarkerEntities?.length)
      this._unfoldedMarkerEntities.forEach((entity) =>
        this._viewer.entities.remove(entity),
      );
    this._unfoldedMarkerEntities = [];
    this._viewer?.scene.requestRender();
  }

  private destroyTooltip() {
    if (this._viewer && this._tooltipEntity)
      this._viewer.entities.remove(this._tooltipEntity);
    this._tooltipEntity = null;
    this._viewer?.scene.requestRender();
  }
}
